import React, { useEffect, useState } from 'react';
import {
  Platform, StyleSheet, Text, TextInput, View, ActivityIndicator, KeyboardAvoidingView,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import Button from '../Button';
import updateTemplateGroup from '../../requests/Assets/UpdateTemplateGroup';
import nameRegex from '../Regex';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import isMobile from '../IsMobile';
import CloseModalIcon from '../CloseModalIcon';
import SuccessMessage from '../SuccessMessage';
import { TemplateGroup } from '../../constants/Types';
import PressableStyled from '../PressableStyled';
import {
  TEMPLATE_GROUP_NAME_DUPLICATE, TEMPLATE_GROUP_NAME_EXCESS, TEMPLATE_GROUP_NAME_INVALID,
  templateGroupNameMaxLength,
} from '../../constants/AlertMessages';

interface AddTemplateGroupModalProps {
  isVisible: boolean,
  hide: () => void,
  id?: string,
  currentName?: string,
  // eslint-disable-next-line no-unused-vars
  update?: (name: string, id: string, create: boolean) => void,
  templateGroups: TemplateGroup[],
}

const AddTemplateGroupModal = ({
  isVisible, hide, id = null, currentName = '', update, templateGroups,
}: AddTemplateGroupModalProps) => {
  const [value, onChangeText] = useState<string | undefined>(currentName);
  const [inputError, setInputError] = useState<string>('');
  const [updateError, setUpdateError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [complete, setComplete] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const { bottom: safeAreaBottom, top: paddingTop } = useSafeAreaInsets();

  useEffect(() => {
    if (isVisible) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    }
  }, [isVisible]);

  const closeModal = () => (visibilityToggleEnabled ? hide() : null);

  const updateGroup = () => {
    setLoading(true);
    updateTemplateGroup(value.trim(), id).then((response) => {
      let isValid: boolean = response && !response.isError;
      let newID: string = id;
      if (!id) {
        newID = response?.msg?.ButtonPrototypeGroupId;
        isValid = newID?.length > 0;
      }
      if (isValid) {
        update(value.trim(), id ?? newID, !id);
        setComplete(true);
      } else setUpdateError(response?.errorText);
      setLoading(false);
    }).catch((error) => {
      setLoading(false);
      setUpdateError(error?.message);
    });
  };

  const onModalHide = () => {
    onChangeText(currentName);
    setInputError('');
    setUpdateError('');
    setLoading(false);
    setComplete(false);
    setInputFocused(false);
    setVisibilityToggleEnabled(true);
  };

  const validateInput = (text: string) => {
    setUpdateError('');
    if (!(text.trim().length > 0)) setInputError('Can\'t be empty');
    else if (text.trim().length > templateGroupNameMaxLength) {
      setInputError(TEMPLATE_GROUP_NAME_EXCESS);
    } else if (!(nameRegex.exp.test(text.trim()))) {
      setInputError(TEMPLATE_GROUP_NAME_INVALID);
    } else if (templateGroups?.filter((group: TemplateGroup) => (!id
      || id !== group.ButtonPrototypeGroupId) && group.Name?.trim() === text.trim()).length > 0) {
      setInputError(TEMPLATE_GROUP_NAME_DUPLICATE);
    } else setInputError('');
  };

  return <Modal {...{ isVisible, onModalHide }}
    onBackButtonPress={hide}
    onSwipeComplete={hide}
    onBackdropPress={closeModal}
    style={[
      Style.modal,
      styles.modal,
      { paddingTop },
      isMobile && Platform.OS === 'android' && styles.androidModal,
    ]}
    animationIn={'slideInUp'}
    animationOut={'slideOutDown'}
  >
    <KeyboardAvoidingView
      behavior={isMobile ? 'padding' : undefined}
      style={isMobile ? styles.keyboardAvoidingView : undefined}
    >
      <View
        style={[
          Style.modalContent,
          isMobile ? styles.containerMobile : styles.containerWeb,
          isMobile && { paddingBottom: styles.containerMobile.paddingBottom + safeAreaBottom },
        ]}
        testID='edit-tmplt-group-modal'>
        {(isMobile || !complete)
          && <Text style={[styles.text, styles.title, !isMobile && styles.titleWeb]}>
            {`${id ? 'Edit' : 'Add'} template group`}
          </Text>}
        {complete ? <SuccessMessage
          title={`The template group${id ? '\'s name' : ''} has been ${id ? 'updated' : 'added'}`}
          testID='successfully-created-templategroup-modal'
        /> : <>
          <Text style={[styles.text, styles.label]}>{'Name'}</Text>
          <TextInput
            {...{ value }}
            maxLength={templateGroupNameMaxLength}
            onChangeText={(text: string) => {
              onChangeText(text);
              validateInput(text);
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder='Enter name'
            placeholderTextColor={styles.text.color}
            style={[
              styles.text, styles.input,
              inputFocused && styles.inputFocused,
              inputError && styles.inputError,
            ]}
            editable={!loading}
          />
          {inputError && <Text style={styles.errorText}>{inputError}</Text>}
          {updateError && <Text style={styles.errorText}>{updateError}</Text>}
          {isMobile ? <Button
            affirmative
            disabled={loading || inputError?.length > 0 || !(value?.length > 0)}
            onPress={updateGroup}
            title={`${id ? 'Save' : 'Add group'}`}
            style={styles.button}
          /> : <View style={[Style.row, styles.buttonContainer]}>
            <Button
              affirmative={false}
              onPress={closeModal}
              title='Cancel'
            />
            <Button
              affirmative
              disabled={loading || inputError?.length > 0 || !(value?.length > 0)}
              onPress={updateGroup}
              title={`${id ? 'Save' : 'Add group'}`}
              testID={id ? 'save-tmplt-group-modal-btn' : 'add-group-modal-btn'}
            />
          </View>}
          <ActivityIndicator
            size='large'
            animating={loading}
            style={Style.activityIndicator}
          />
        </>}
        <PressableStyled
          style={isMobile ? styles.closeButton : Style.closeButton}
          onPress={hide}
          testID='close-successfully-created-templategroup-modal'
        >
          {isMobile ? <DeleteIcon24 color={Colors.light.hereworksBlack600} /> : <CloseModalIcon />}
        </PressableStyled>
      </View>
    </KeyboardAvoidingView>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: isMobile ? 'flex-end' : 'center',
  },
  androidModal: {
    paddingBottom: 0,
  },
  keyboardAvoidingView: {
    width: '100%',
  },
  containerMobile: {
    alignSelf: isMobile ? 'stretch' : 'center',
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 32,
    borderBottomStartRadius: 0,
    borderBottomEndRadius: 0,
  },
  containerWeb: {
    alignSelf: 'center',
    padding: 40,
    paddingTop: 60,
    width: 530,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  title: {
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    alignSelf: 'center',
    textAlign: 'center',
  },
  titleWeb: {
    fontSize: 28,
    lineHeight: 35,
  },
  label: {
    marginTop: 39,
    marginBottom: 4,
  },
  input: {
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    color: Colors.light.hereworksBlack600,
    outlineWidth: 0,
  },
  inputError: {
    borderColor: Colors.light.hereworksRed500,
  },
  inputFocused: {
    borderColor: Colors.light.hereworksBlue400,
  },
  errorText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
    marginTop: 4,
  },
  button: {
    flex: undefined,
    alignSelf: 'stretch',
    marginTop: 24,
    marginStart: undefined,
  },
  successMessage: {
    marginTop: isMobile ? 24 : 32,
    fontSize: isMobile ? 20 : 28,
    lineHeight: isMobile ? 24 : 34,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksGreen400,
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    end: 24,
    top: 14,
  },
  buttonContainer: {
    alignSelf: 'center',
    marginTop: 40,
  },
});

export default AddTemplateGroupModal;
